import Link from 'next/link'
import { CarouselContext } from 'pure-react-carousel'
import { stringifyUrl } from 'query-string'
import { ReactElement, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import {
  Button,
  Dropdown as SizeSelector,
  ViewportSizes,
  useIsViewport,
} from '@syconium/little-miss-figgy'

import { useLocalization } from '../../../../app/_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import { CartContainer } from '../../../../brunswick/containers/cart'
import { trackEvent } from '../../../../lib/analytics'
import { PriceDisplay } from '../../PriceDisplay'

import {
  AddButtonWrapper,
  Controls,
  DetailText,
  Details,
  PriceAndColor,
  ProductName,
  SizeSelectorWrapper,
  UpsellTile as StyledTile,
  Thumbnail,
  TileOverview,
} from './styles'

import type { UpsellProduct } from './types'

const SIZE_SELECTOR_OPTION_HEIGHT: number = 29 // px
const SIZE_SELECTOR_BUTTON_HEIGHT: number = 24 // px

export const UpsellTile = ({
  nostoPlacementId,
  product,
  i,
}: {
  nostoPlacementId?: string
  product: UpsellProduct
  i: number
}): ReactElement => {
  const { region } = useLocalization()
  const { addItems } = CartContainer.useContainer()
  const { cart: miniCartCopy } = useTranslation()

  const variants = useMemo(() => {
    return product.variants ?? []
  }, [product.variants])

  const [windowInnerHeight, setWindowInnerHeight] = useState<number>(() => {
    return typeof window !== 'undefined' ? window.innerHeight : 0
  })
  const [selectedSize, setSelectedSize] = useState<string>('')
  const [sizeSelectorOptions, setSizeSelectorOptions] = useState<string[]>([])
  const [sizeSelectorYOffset, setSizeSelectorYOffset] = useState<number>(0)
  // This is to allow for flexibility wrt sizes available for various products. An individual cell height
  // (in the size-selector dropdown) is ~29px, so we multiply that number by the number of items in the sizes array.
  // This gives us a reasonable height that we can use to determine if we should open the dropdown
  // upwards or downwards.
  const [sizeSelectorHeight, setSizeSelectorHeight] = useState<number>(
    SIZE_SELECTOR_OPTION_HEIGHT * sizeSelectorOptions.length + SIZE_SELECTOR_BUTTON_HEIGHT
  )

  const sizeSelectorWrapperRef = useRef<HTMLDivElement | null>(null)
  const syncDimensions = () => {
    setWindowInnerHeight(typeof window !== 'undefined' ? window.innerHeight : 0)
    setSizeSelectorYOffset(sizeSelectorWrapperRef.current?.getBoundingClientRect().y ?? 0)
  }

  const carouselContext = useContext(CarouselContext)
  const [currentSlide, setCurrentSlide] = useState(carouselContext.state.currentSlide)

  useEffect(() => {
    function onChange() {
      setCurrentSlide(carouselContext.state.currentSlide)
    }
    carouselContext.subscribe(onChange)
    return () => carouselContext.unsubscribe(onChange)
  }, [carouselContext])

  useEffect(() => {
    // nested for-loop because we want to display garment sizes in the dropdown
    // and also maintain the order the sizes are returned from in the query; e.g.
    // [S, M, L,...]
    const sizes = product.productGroup.sizes
    if (sizes.length === 0) {
      setSizeSelectorOptions(['O/S'])
    } else {
      const sizesWithSoldOutStatus: Set<string> = new Set()
      for (let q = 0; q < sizes.length; q++) {
        for (let i = 0; i < variants.length; i++) {
          const size = sizes[q]
          const variant = variants[i]
          if (
            variant &&
            size &&
            !sizesWithSoldOutStatus.has(`${size} ${miniCartCopy.soldOutLabel}`) &&
            !sizesWithSoldOutStatus.has(size)
          ) {
            if (variant.size === size && variant.soldOut) {
              sizesWithSoldOutStatus.add(`${size} ${miniCartCopy.soldOutLabel}`)
            } else if (variant.size === size) {
              sizesWithSoldOutStatus.add(size)
            }
          }
        }
      }
      setSizeSelectorOptions(Array.from(sizesWithSoldOutStatus))
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product])

  const sizeSelectorOpeningDirection: 'up' | 'down' =
    useIsViewport(ViewportSizes.large) ||
    windowInnerHeight < sizeSelectorHeight + sizeSelectorYOffset
      ? 'up'
      : 'down'
  useEffect(() => {
    setSizeSelectorHeight(
      SIZE_SELECTOR_BUTTON_HEIGHT +
        // 12px, here, is an allowance for border/margin additions
        (sizeSelectorOpeningDirection === 'up' ? 12 : 0) +
        SIZE_SELECTOR_OPTION_HEIGHT * sizeSelectorOptions.length
    )
  }, [sizeSelectorOpeningDirection, sizeSelectorOptions.length])

  useEffect(() => {
    syncDimensions()
    window.addEventListener('resize', syncDimensions, { passive: true })
    window.addEventListener('scroll', syncDimensions, { passive: true })

    return () => {
      window.removeEventListener('resize', syncDimensions)
      window.removeEventListener('scroll', syncDimensions)
    }
  }, [])

  const { ref: setInViewRef, inView } = useInView({
    threshold: 0,
    triggerOnce: true,
    fallbackInView: true,
  })
  const onSizeSelectorWrapperElementChange = useCallback(
    (newElement: HTMLDivElement) => {
      sizeSelectorWrapperRef.current = newElement
      setInViewRef(newElement)
    },
    [setInViewRef]
  )

  useEffect(() => {
    setSizeSelectorYOffset(sizeSelectorWrapperRef?.current?.getBoundingClientRect().y ?? 0)
  }, [inView])

  useEffect(() => {
    const size = variants[0]?.size
    if (size && variants.length === 1) {
      setSelectedProductVariantBySize(size)
    }
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product])

  const setSelectedProductVariantBySize = useMemo(() => {
    return (size: string) => {
      setSelectedSize(size)
    }
  }, [])
  const selectedVariant = useMemo(
    () =>
      variants.find(i => {
        // get the variant for the selected size if it exists.
        // Otherwise, return the variant object that matches the default variant size
        return i.size === (selectedSize || product.productGroup.defaultVariant?.size)
      }),
    [product.productGroup.defaultVariant?.size, selectedSize, variants]
  )

  const focusedItem: Parameters<typeof addItems>[0][number] = {
    finalSale: product.finalSale,
    swPromoEligible: product.swPromoEligible,
    item: {
      category: product.productGroup.category,
      currencyType: region.currency,
      externalParentId: product.productGroup.defaultVariant?.externalParentId ?? '',
      discountPrice:
        product.productGroup.defaultVariant?.priceDetails?.discountPrice?.amount ?? null,
      shopifyId: selectedVariant?.shopifyId ?? '',
      handle: product.handle ?? '',
      sku: selectedVariant?.sku ?? '',
    },
  }

  const prettyTitle: string = `${product?.productGroup?.style ?? ''} ${
    product?.productGroup?.description ?? ''
  }`

  const productHref = stringifyUrl({
    url: `/products/${product.productGroup.handle}`,
    query: { nosto: nostoPlacementId, color: product.colorInfo?.rawName, fit: product.rawFit },
  })

  const price = selectedVariant?.priceDetails?.price?.amount
  const discountPrice = selectedVariant?.priceDetails?.discountPrice?.amount

  return (
    <StyledTile>
      <Link
        href={productHref}
        key={product.handle}
        {...trackEvent({
          category: 'minicart upsell',
          action: 'click product image',
        })}
        tabIndex={currentSlide === i ? 0 : -1}
      >
        <TileOverview>
          <Thumbnail imgUrl={product.images?.nodes[0]?.source ?? ''} />
          <Details>
            <ProductName>{prettyTitle}</ProductName>
            <PriceAndColor>
              <PriceDisplay discountPrice={discountPrice} price={price} priceRange={null} />
              <DetailText>{product.color}</DetailText>
            </PriceAndColor>
          </Details>
        </TileOverview>
      </Link>
      <Controls>
        {Boolean(sizeSelectorOptions.length) && (
          <SizeSelectorWrapper
            openingDirection={sizeSelectorOpeningDirection}
            dropdownHeight={sizeSelectorHeight}
            data-testid='FIGS__Upsells__SizeSelectorWrapper'
            ref={onSizeSelectorWrapperElementChange}
            {...trackEvent({
              category: 'minicart upsell',
              action: 'select size',
            })}
          >
            <SizeSelector
              onChange={x => setSelectedProductVariantBySize(x)}
              options={sizeSelectorOptions}
              selection={selectedSize}
              placeholderText={miniCartCopy.selectSize}
              tabIndex={currentSlide === i ? 0 : -1}
              isScrollable
              {...trackEvent({
                category: 'minicart upsell',
                action: 'click size selector',
                value: selectedSize ?? '',
              })}
            />
          </SizeSelectorWrapper>
        )}

        <AddButtonWrapper>
          <Button
            allCaps
            block
            data-testid='FIGS__Upsells__AddButton'
            disabled={!selectedVariant || selectedVariant?.soldOut}
            onClick={() => {
              addItems([focusedItem])
            }}
            size='large'
            {...trackEvent({
              category: 'minicart upsell',
              action: 'click add to bag',
              label: 'Add to Bag',
            })}
            tabIndex={currentSlide === i ? 0 : -1}
          >
            {miniCartCopy.add}
          </Button>
        </AddButtonWrapper>
      </Controls>
    </StyledTile>
  )
}
